/**
 * Compliance domain schemas, derived from the generated Zod. One schema serves
 * both the retrieve and list reads (identical shapes).
 *
 * `elevatorType` is INHERITED from the generated `Car.ElevatorType.choices`
 * union, not narrowed to a hand-written list.
 *
 * See ./README.md.
 */
import { z } from 'zod';

import {
  CompliancesDefectAnalyticsRetrieveResponse,
  CompliancesListResponse,
  CompliancesRetrieveResponse,
} from '~/data/generated/zod/compliances/compliances.zod';
import { ViolationNotesListResponse } from '~/data/generated/zod/violation-notes/violation-notes.zod';

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- value exists only to derive the type below via z.infer
const api_complianceMetadataSchema = CompliancesListResponse.shape.metadata;

// propertyManager and serviceProviderId both inherit the generated base: both
// are `allow_null=True` at source as of AMP-633 (wire emits null when the
// property has no property-manager group / the car has no currently-active
// service-provider contract), so the generated `.nullable()` types match.
export const api_complianceSchema = CompliancesRetrieveResponse;

// `count`/`next`/`previous` are genuinely optional on the wire: without a
// `limit` the list returns just `{metadata, results}`. Harden the consumer
// rather than forcing them required here.
export const api_complianceFilterResponse = CompliancesListResponse.extend({
  results: z.array(api_complianceSchema),
});

///////////////////////////////////////////////////////////////////////////////
// Violation notes
//
// The endpoint sets `pagination_class = None`, so the flat `{metadata, results}`
// envelope is the only reachable response shape.
const violationNoteSchema = ViolationNotesListResponse.shape.results.element.extend({
  // Drops the generated `.email()`: an emailless user serializes as `""`, which
  // `.email()` rejects. Only ever rendered as an author label, never parsed.
  userEmail: z.string(),
});

export const violationNotesResponseSchema = ViolationNotesListResponse.extend({
  results: z.array(violationNoteSchema),
});

///////////////////////////////////////////////////////////////////////////////
// Defect analytics
//
// One payload backing the three panels on the UK (LOLER) compliance overview.
// The endpoint returns a fixed-shape object, so the generated schema is used
// as-is — every field is required and non-nullable on the wire. (CX-416)
export const api_defectAnalyticsSchema = CompliancesDefectAnalyticsRetrieveResponse;

export type API_DefectAnalytics = z.infer<typeof api_defectAnalyticsSchema>;
export type API_DefectTrendPoint = API_DefectAnalytics['openVsClosed'][number];
export type API_DefectSeverityBreakdown = API_DefectAnalytics['openBySeverity'];
export type API_DefectDueBucket = API_DefectAnalytics['upcomingDue'][number];

export type API_ComplianceMetadata = z.infer<typeof api_complianceMetadataSchema>;
export type API_Compliance = z.infer<typeof api_complianceSchema>;
export type API_ComplianceFilterResponse = z.infer<typeof api_complianceFilterResponse>;
